import { z } from "zod";
import { ActiveCoachTaskSchema, isPublishConfirmationPassing, type ActiveCoachTask } from "./index";
import type { GrowthPlatform, GrowthTaskKind } from "./growth";

export const ConfirmGrowthOutcomeRequestSchema = z.object({ taskId: z.string().min(1).max(120), confirmed: z.literal(true) });
/**
 * 需要用户确认成果、而不是靠"关联一份已完成任务"就能通过的阶段。
 * `product_launch` 在列：草稿、填写完成和真实发布必须是三件不同的事。
 * 旧三类仍在列，供未迁移的历史路线继续走原口径。
 */
export const isOutcomeGrowthStage = (kind: GrowthTaskKind) =>
  ["product_launch", "product_feasibility", "supplier_profit", "listing_preparation"].includes(kind);
export const normalizeGrowthPlatform = (platform: string | null) => platform?.toLowerCase().replace(/[\s_-]/g, "") || "";
export function growthStageGoal(kind: GrowthTaskKind, platform: string): string {
  const goals: Partial<Record<GrowthTaskKind, string>> = {
    product_launch: "请在同一个商品任务里走完选品到上架：先核对商品事实与需求、竞争、评价痛点、价格带四条市场信号，再确认供应商与利润，然后生成 Listing 草稿并进入目标平台发布页；只有你确认真实发布结果后，这个阶段才算完成。",
    product_feasibility: "请基于当前真实商品完成商品可行性评估，说明依据、风险、缺失信息和下一步；资料不足时给出有条件或信息不足的结论。",
    supplier_profit: "请继续当前商品，整理供应商核对清单并计算利润；列出已知成本、缺失报价和测算条件。",
    listing_preparation: "请继续当前商品，基于已核实的资料生成 Listing 草稿，列出标题、卖点、描述、属性、图片要求和待核验项。",
    fulfillment_service: "请整理当前商品的履约与售后方案，说明发货、物流和买家问题处理所需信息。",
    growth_operations: "请完成当前商品的投放前检查和保本 ROAS 测算，明确缺失的数据。",
    independent_review: "请换一个实际商品，独立完成选品、供应商核对、利润核算和 Listing 草稿，保留货源详情链接。",
  };
  return `目标平台：${platform}。${goals[kind] || "请继续当前开店步骤。"}`;
}

export interface GrowthStageOutcome {
  eligible: boolean;
  error?: "EVIDENCE_PLATFORM_MISMATCH" | "COACH_TASK_EVIDENCE_INCOMPLETE" | "COACH_TASK_UNAVAILABLE";
  title: string;
  summary: string;
  facts: string[];
  missing: string[];
  links: { label: string; url: string }[];
}
const httpUrl = (raw: string): string | null => { try { const url = new URL(raw); return ["http:", "https:"].includes(url.protocol) ? url.href : null; } catch { return null; } };

/** Derive only persisted task content. A stage report need not finish the whole coach workflow. */
export function deriveGrowthStageOutcome(kind: GrowthTaskKind, platform: GrowthPlatform | null, value: unknown, lifecycle: string): GrowthStageOutcome {
  const out: GrowthStageOutcome = { eligible: false, title: "阶段成果", summary: "尚未形成当前阶段的成果。", facts: [], missing: [], links: [] };
  if (!["active", "completed"].includes(lifecycle)) return { ...out, error: "COACH_TASK_UNAVAILABLE", missing: ["这条任务已收起或不可用，请继续可用任务。"] };
  const parsed = ActiveCoachTaskSchema.safeParse(value);
  if (!parsed.success || parsed.data.taskType !== "product_listing") return { ...out, error: "COACH_TASK_EVIDENCE_INCOMPLETE", missing: ["缺少格式完整的商品任务记录。"] };
  const task = parsed.data;
  if (!platform || normalizeGrowthPlatform(task.destinationPlatform) !== normalizeGrowthPlatform(platform)) return { ...out, error: "EVIDENCE_PLATFORM_MISMATCH", missing: ["任务目标平台与当前路线不同。"] };
  const source = httpUrl(task.sourcePage.url);
  if (source) out.links.push({ label: "货源详情", url: source });
  if (kind === "product_launch") {
    /**
     * 统一阶段的成果 = 选品初筛 + 供应商 + 利润 + Listing 草稿，**加上**真实发布回执。
     *
     * 前四项都是中间状态：旧版路线在三个阶段都完成时也只证明"Listing 准备完成"，
     * 迁移后的定位就是这里。只有 `published` / `in_review` 两种用户确认过的平台状态
     * 才让阶段通过——草稿和填写完成永远不显示为"已上架"。
     */
    out.title = "选品到上架";
    const assessment = task.selectionAssessment;
    if (assessment) {
      out.facts.push(...assessment.evidence.map(fact => `${fact.label}：${fact.value}`));
      for (const signal of assessment.marketSignals) {
        out.facts.push(`${signal.verification === "sourced" ? "有来源记录" : "待外部验证"}：${signal.finding}`);
        out.missing.push(...signal.verification === "sourced" ? [] : [signal.finding]);
      }
    } else out.missing.push("尚未形成商品初筛记录。");
    const launchChecklist = task.supplierChecklist || [];
    out.missing.push(...launchChecklist.filter(item => item.status !== "confirmed").map(item => item.question));
    if (!launchChecklist.length) out.missing.push("尚未形成供应商核对清单。");
    const launchProfit = task.profitResult;
    if (launchProfit) {
      const numbers = [["收入", launchProfit.revenue], ["总成本", launchProfit.totalCost], ["利润", launchProfit.profit], ["保本售价", launchProfit.breakEvenPrice]] as const;
      out.facts.push(...numbers.filter(([, value]) => value !== null).map(([label, value]) => `${label}：¥${value}`));
      out.missing.push(...launchProfit.missingInputs);
    } else out.missing.push("尚未形成利润测算或缺项报告。");
    const launchDraft = task.listingDraft;
    const draftMatches = Boolean(launchDraft && normalizeGrowthPlatform(launchDraft.targetPlatform) === normalizeGrowthPlatform(platform));
    if (launchDraft && draftMatches) {
      out.facts.push(`Listing 草稿：${launchDraft.title}`);
      out.missing.push(...launchDraft.warnings);
    } else out.missing.push("尚未形成当前首发平台的 Listing 草稿。");
    const confirmation = task.publishConfirmation;
    if (!confirmation) {
      out.summary = "选品、供应商、利润与 Listing 草稿都已记录，但还没有你确认的商品实际发布结果。";
      out.missing.push("请在平台确认商品实际是草稿、审核中还是已发布；草稿和填写完成不算已上架。");
    } else if (isPublishConfirmationPassing(confirmation)) {
      out.summary = confirmation.observedState === "published"
        ? "你已确认真实发布结果：商品已在平台发布。"
        : "你已确认真实发布结果：商品正在平台审核中。";
      out.facts.push(confirmation.observedState === "published" ? "已发布" : "审核中");
      if (confirmation.platformProductId) out.facts.push(`平台商品 ID：${confirmation.platformProductId}`);
      if (confirmation.note) out.facts.push(`你的备注：${confirmation.note}`);
    } else {
      out.summary = confirmation.observedState === "draft_saved"
        ? "你确认平台里目前只保存了草稿，商品还没有发布。"
        : "你确认目前还无法判断商品在平台里的实际状态。";
      out.missing.push("平台里目前只是草稿或状态未知，请完成发布后再来确认。");
    }
    out.eligible = Boolean(
      assessment?.summary.trim() && assessment.evidence.some(fact => fact.label.trim() && fact.value.trim())
      && launchChecklist.length && launchProfit && draftMatches && isPublishConfirmationPassing(confirmation),
    );
  } else if (kind === "product_feasibility") {
    out.title = "商品可行性评估";
    const assessment = task.selectionAssessment;
    if (assessment) {
      out.summary = `${{ suitable: "初筛适合", conditional: "有条件继续", insufficient: "信息不足" }[assessment.verdict]}：${assessment.summary}`;
      out.facts.push(...assessment.evidence.map(fact => `${fact.label}：${fact.value}`), ...assessment.strengths.map(value => `优势：${value}`), ...assessment.risks.map(value => `风险：${value}`));
      for (const signal of assessment.marketSignals) {
        out.facts.push(`${signal.verification === "sourced" ? "有来源记录" : "待外部验证"}：${signal.finding}`);
        const url = signal.sourceRef && httpUrl(signal.sourceRef);
        if (url) out.links.push({ label: "市场信号来源", url });
        else if (signal.sourceRef) out.facts.push(`来源记录：${signal.sourceRef}`);
      }
      out.missing.push(...task.missingInformation, ...assessment.marketSignals.filter(signal => signal.verification !== "sourced").map(signal => signal.finding));
      if (!assessment.marketSignals.some(signal => signal.verification === "sourced")) out.missing.push("尚无带来源的市场信号，需求、竞争与价格带仍需核验。");
      const substantive = assessment.evidence.some(fact => fact.label.trim() && fact.value.trim())
        || [...assessment.strengths, ...assessment.risks, ...assessment.marketSignals.map(signal => signal.finding)].some(fact => fact.trim());
      out.eligible = Boolean(assessment.summary.trim() && substantive);
    }
    if (!out.eligible) out.missing.push("需要包含判断依据或风险说明的商品评估。");
  } else if (kind === "supplier_profit") {
    out.title = "供应商与利润报告";
    const checklist = task.supplierChecklist || [];
    out.facts.push(...checklist.map(item => `${{ confirmed: "已确认", unknown: "待确认", issue: "有问题" }[item.status]} · ${item.question}：${item.note || item.reason}`));
    out.missing.push(...checklist.filter(item => item.status !== "confirmed").map(item => item.question));
    const profit = task.profitResult;
    if (profit) {
      out.summary = profit.complete ? "已形成利润测算，请核对成本与假设。" : "已形成部分测算，补齐报价后才能判断利润。";
      const numbers = [["收入", profit.revenue], ["总成本", profit.totalCost], ["利润", profit.profit], ["保本售价", profit.breakEvenPrice]] as const;
      out.facts.push(...numbers.filter(([, value]) => value !== null).map(([label, value]) => `${label}：¥${value}`), ...profit.warnings.map(value => `注意：${value}`));
      out.missing.push(...profit.missingInputs);
    }
    if (!checklist.length) out.missing.push("尚未形成供应商核对清单。");
    if (!profit) out.missing.push("尚未形成利润测算或缺项报告。");
    out.eligible = Boolean(checklist.some(item => item.question.trim() && item.reason.trim()) && profit && (profit.complete ? profit.profit !== null && profit.missingInputs.length === 0 : profit.missingInputs.some(value => value.trim())));
  } else if (kind === "listing_preparation") {
    out.title = "Listing 草稿";
    const draft = task.listingDraft;
    if (draft && normalizeGrowthPlatform(draft.targetPlatform) === normalizeGrowthPlatform(platform)) {
      out.summary = draft.title;
      out.facts.push(...draft.highlights.map(value => `卖点：${value}`), draft.description, ...draft.attributes.map(fact => `${fact.label}：${fact.value}`), ...draft.imageChecklist.map(value => `图片：${value}`));
      out.missing.push(...draft.warnings);
      out.eligible = Boolean(draft.title.trim() && draft.description.trim());
    } else out.missing.push("尚未形成当前首发平台的 Listing 草稿。");
  } else out.missing.push("当前阶段尚无可用于确认的成果契约，请在陪跑任务中继续整理实际结果。");
  out.missing = [...new Set(out.missing)];
  if (!out.eligible) out.error = "COACH_TASK_EVIDENCE_INCOMPLETE";
  return out;
}

export function growthTaskMatchesPlatform(task: ActiveCoachTask, platform: GrowthPlatform | null): boolean {
  return Boolean(platform && normalizeGrowthPlatform(task.taskType === "platform_registration" ? task.platform : task.destinationPlatform) === normalizeGrowthPlatform(platform));
}
